import {LightWeightApplication, lightWeightApplicationSchema} from "../Application";
import {array, number, object, ObjectSchema, string} from "yup";
import {fieldDefinedError} from "../../../shared/technical/rest/validation";
import {Company, companySchema} from "../../companies/Company";
import {ValidityInfo, validityInfoSchema} from "../../../shared/technical/domain/ValidityInfo";
import {TFunction} from "i18next";

export interface License {
  id?: string,
  application: LightWeightApplication,
  company: Company,
  serverLicense?: ServerLicense,
  quotaLicenses?: QuotaLicense[],
  userQuotaLicense?: UserQuotaLicense,
  validityInfo: ValidityInfo,
}

export interface ServerLicense {
  name: string,
  key?: string,
  allowedUris?: string[],
}

export interface QuotaLicense {
  name: string,
  quota: number,
  usage?: number,
}

export interface UserQuotaLicense {
  quota: number,
  usage?: number,
}

export function serverLicenseSchema(t: TFunction): ObjectSchema<ServerLicense> {
  return object({
    name: string().trim().required(fieldDefinedError(t)),
    key: string(),
    allowedUris: array(string().trim().defined()),
  })
}

export function quotaLicenseSchema(t: TFunction): ObjectSchema<QuotaLicense> {
  return object({
    name: string().trim().required(fieldDefinedError(t)),
    quota: number().required(fieldDefinedError(t)),
    usage: number(),
  })
}

export function userQuotaLicenseSchema(t: TFunction): ObjectSchema<UserQuotaLicense> {
  return object({
    quota: number().required(fieldDefinedError(t)),
    usage: number(),
  })
}

export function licenseSchema(t: TFunction): ObjectSchema<License> {
  return object({
    id: string(),
    application: lightWeightApplicationSchema,
    company: companySchema(t).required(fieldDefinedError(t)),
    serverLicense: serverLicenseSchema(t).transform(value => value.name ? value : undefined).default(undefined),
    quotaLicenses: array(quotaLicenseSchema(t).transform(value => value.quota ? value : undefined).default(undefined)),
    userQuotaLicense: userQuotaLicenseSchema(t).transform(value => value.quota ? value : undefined).default(undefined),
    validityInfo: validityInfoSchema(t)
  })
}

export function defaultLicense(application: LightWeightApplication, t: TFunction) {
  return {...licenseSchema(t).getDefault(), application: application} as License
}

export function defaultQuotaLicense(t: TFunction) {
  return quotaLicenseSchema(t).getDefault()
}

export function formatQuotaLicenses(quotaLicenses: QuotaLicense[]) {
  return quotaLicenses.map(quotaLicense => formatQuotaLicense(quotaLicense)).join(", ");
}

export function formatQuotaLicense(quotaLicense: QuotaLicense) {
  return quotaLicense.name + ": " + quotaLicense.usage + " / " + quotaLicense.quota;
}

export function formatUserQuotaLicense(userQuotaLicense: UserQuotaLicense) {
  return userQuotaLicense.usage + " / " + userQuotaLicense.quota;
}
